import type { SubTopic, WasteCard } from '../data/cards';
import type { BinId } from '../data/bins';

export interface AnsweredEntry {
  card: WasteCard;
  chosenBin: BinId;
  correct: boolean;
  /** True if this card's final result came from the second (post-hint) attempt. */
  hintUsed: boolean;
}

export interface SubTopicStat {
  correct: number;
  total: number;
}

/** Tracks which cards were scanned this session — feeds the end-of-session SummaryPanel. */
export class RoundState {
  private readonly answered: AnsweredEntry[] = [];

  record(entry: AnsweredEntry): void {
    this.answered.push(entry);
  }

  hasSeen(cardId: string): boolean {
    return this.answered.some((entry) => entry.card.id === cardId);
  }

  getEntries(): readonly AnsweredEntry[] {
    return this.answered;
  }

  /** Aggregates correct/total per sub-topic — a card can count toward more than one. */
  getSubTopicStats(): Record<SubTopic, SubTopicStat> {
    const stats: Record<SubTopic, SubTopicStat> = {
      reduction_practice: { correct: 0, total: 0 },
      awareness_capacity: { correct: 0, total: 0 },
      circular_economy: { correct: 0, total: 0 },
    };
    for (const entry of this.answered) {
      for (const topic of entry.card.subTopics) {
        stats[topic].total += 1;
        if (entry.correct) stats[topic].correct += 1;
      }
    }
    return stats;
  }

  /** Cards whose final recorded result was incorrect (hint-assisted corrections are excluded). */
  getWrongCards(): WasteCard[] {
    return this.answered.filter((entry) => !entry.correct).map((entry) => entry.card);
  }

  reset(): void {
    this.answered.length = 0;
  }
}
